import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { SessionProvider } from "./SessionContext";
import { router } from "./router";
import { Button } from "../design/components";
import { DemoGateway, useCover } from "../demo/DemoGateway";
import { DataSourceError } from "../api/dataSource";
import { config } from "../config/env";
import { I18nProvider, t } from "../i18n";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <DemoGateway>
          <SessionProvider
            fallback={<Gate>{t("Connecting…")}</Gate>}
            failure={(error, retry) => <Gate error={error} retry={retry} />}
          >
            <RouterProvider router={router} />
          </SessionProvider>
        </DemoGateway>
      </I18nProvider>
    </QueryClientProvider>
  );
}

/** Start-up gate: shown until the bootstrap answers, or when it never does. */
export function Gate({ children, error, retry }: { children?: React.ReactNode; error?: Error; retry?: () => void }) {
  const { user, signOut } = useCover();
  const status = error instanceof DataSourceError ? error.status : undefined;
  /*
   * Signed in as a person, a refusal is about them, not the connection: the
   * platform knows who they are and has given them no role (403), or does not
   * accept the sign-in at all (401). Neither is fixed by an .env file, and the
   * way out is signing out, not trying again.
   */
  if (config.identity && (status === 401 || status === 403)) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{ maxWidth: 480, textAlign: "center", display: "grid", gap: 12 }}>
          <div className="eyebrow">{t("Sky Logics · operations control")}</div>
          <h1>{status === 403 ? t("Signed in — no access yet") : t("The platform did not accept this sign-in")}</h1>
          <p className="muted" role="alert">
            {status === 403
              ? t(
                  "You are signed in as {name}, but no role has been given to this account. An administrator assigns one under Administration › People; sign in again once they have.",
                  { name: user?.name ?? "" },
                )
              : t(
                  "The sign-in worked, but the platform does not recognize it. This usually means the sign-in provider has not been registered with the platform yet. Tell your administrator.",
                )}
          </p>
          <div>
            <Button variant="primary" onClick={signOut}>
              {t("Sign out")}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ maxWidth: 480, textAlign: "center", display: "grid", gap: 12 }}>
        <div className="eyebrow">{t("Sky Logics · operations control")}</div>
        {error ? (
          <>
            <h1>{t("Could not start")}</h1>
            <p className="muted" role="alert">
              {error.message}
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              {t(
                "Check VITE_SLT_API_URL and the credential in .env.local, or set VITE_DATA_SOURCE=mock to run on the built-in sample.",
              )}
            </p>
            {retry && (
              <div>
                <Button variant="primary" onClick={retry}>
                  {t("Try again")}
                </Button>
              </div>
            )}
          </>
        ) : (
          <p className="muted">{children}</p>
        )}
      </div>
    </div>
  );
}
